// React
import type React from 'react';
import type { JSX } from 'react';
import dynamic from 'next/dynamic';

// Constants
import { Microsites, SectionTitles, SectionTypes } from 'constants/constants';
import SECTION_PAGE_TEST_IDS from 'constants/testsIds/sectionPage';
import HUBPAGE_TEST_IDS from 'constants/testsIds/hubpage';
import getCommonTestId from 'constants/testsIds/common';

// Interfaces
import type { Hub } from 'interfaces/Home';
import type { SectionFeaturedArticles } from 'api/Section/getFeaturedArticlesBySection';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import type { MenuItemProps } from 'interfaces/navigation/Header';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { RelatedRanking } from 'interfaces/content/Ranking';
import { SectionVariant } from 'interfaces/Section';

// Components
import ErrorBoundary from 'components/ErrorBoundary';
import ArticleSection from 'components/Sections/ArticleSection';
import CategoryChild from 'components/Sections/CategoryChild';
import HubHeader from 'components/HubHeader';
import SectionTitle from 'components/Globals/SectionTitle';
import { RightRailFlex } from 'components/Ad/AdSlot';
import { RightRailStickyAd } from 'components/Ad/AdSlot/Styles';
import AdSlotToggle from 'components/Ad/AdSlot/AdSlotToggle';
import ProductHomePageInStream from 'components/Ad/AdSlot/ProductHomePageInStream';
import Breadcrumbs from 'components/Breadcrumbs';
import LatestStories from 'components/LatestStories';
import Banner from 'components/Banner';
import MoreRankings from 'components/Ranking/MoreRankings';

// Hooks
import useBreakpoint from 'hooks/useBreakpoint';

// Utils
import { UIFragments } from 'utils/log/constants/uiFragments';
import RawHtml from 'utils/miscUtils/rawHtml';
import { generateNativoLatest, getNativoChildrenCategory } from 'utils/miscUtils/nativoHelpers';

// Styles
import * as S from './SectionHubPageStyles';

export interface SectionHubPageProps {
  data: Hub;
  dataLatest: ArticlePreview[];
  pagination?: PagePagination;
  sidebar?: (JSX.Element | null)[];
  hideAds?: boolean;
  subMenu?: MenuItemProps[];
  relatedRankings?: RelatedRanking[];
  featuredArticles?: SectionFeaturedArticles;
}

const LatestArticles = dynamic(() => import('components/recommends/ArticleSection/LatestArticles'));

function cleanDomain(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

export function getBreadcrumbsData(data: Hub): { label: string; link: string }[] {
  const hub = data as Hub;
  if (hub.breadcrumbs?.items.length) {
    return [
      { label: 'home', link: '/' },
      ...hub.breadcrumbs.items.map((item) => ({
        label: item.label,
        link: cleanDomain(item.link) ?? '',
      })),
    ];
  }
  return [
    { label: 'home', link: '/' },
    { label: data.name, link: '' },
  ];
}
// Helper to get submenu items
function getAllSubmenuItems(data: Hub, subMenu?: MenuItemProps[]): MenuItemProps[] {
  const hub = data;

  // Extract topics and tags with consistent structure
  const topicChildren =
    hub?.childTopics?.map((topic) => ({
      id: topic.slug,
      label: topic.name,
      url: topic.permalink,
      slug: topic.slug,
    })) ?? [];

  const tagChildren =
    hub?.childTags?.map((tag) => ({
      id: tag.slug,
      label: tag.name,
      url: tag.permalink,
      slug: tag.slug,
    })) ?? [];

  // Combine topics and tags
  const allChildren = [...topicChildren, ...tagChildren];

  // Create a set of existing URLs for efficient lookup (normalize URLs by removing trailing slashes)
  const existingUrls = new Set([
    ...allChildren.map((child) => child?.url.replace(/\/$/, '')),
    ...(subMenu?.map((item) => item?.url.replace(/\/$/, '')) ?? []),
  ]);

  // Add remaining hub children that aren't already included
  const extraChildren =
    data.children
      ?.filter((child) => !existingUrls.has((child.uri || child.link)?.replace(/\/$/, '')))
      .map((child) => ({
        id: child.slug,
        label: child.name,
        url: child.uri,
        slug: child.slug,
      })) ?? [];

  // Combine all children and sort alphabetically by label
  const allSubmenuChildren = [...allChildren, ...extraChildren].sort((a, b) => a.label.localeCompare(b.label));

  // Return combined submenu with children, or just children if no submenu exists
  return subMenu ? [...subMenu, ...allSubmenuChildren] : allSubmenuChildren;
}

// Helper to get Nativo hub posts
function getNativoHubPosts(hasNativo: boolean, latestArticles: ArticlePreview[], isMdMin: boolean): JSX.Element[] {
  return hasNativo ? generateNativoLatest(latestArticles, isMdMin) : [];
}

const SectionHubPageHeader: React.FC<{
  data: Hub;
  allSubmenuItems: MenuItemProps[];
  sponsor: Hub['sponsor'];
  isSponsorEnable: boolean;
}> = ({ data, allSubmenuItems, sponsor, isSponsorEnable }) => (
  <S.Header>
    <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
      <Breadcrumbs
        isHubPage
        items={getBreadcrumbsData(data)}
      />
      <HubHeader
        title={data.title}
        subMenu={allSubmenuItems}
        sponsor={sponsor ? { ...sponsor, sponsorEnabled: isSponsorEnable, sponsorType: 'sponsor' } : undefined}
      />
    </ErrorBoundary>
  </S.Header>
);

const SectionHubPageDescription: React.FC<{ description?: string }> = ({ description }) =>
  description && description.replace('&nbsp;', '').trim() !== '' ? (
    <ErrorBoundary fragment={UIFragments.HUB_PAGE_DESCRIPTION}>
      <S.HubDescription data-cy={HUBPAGE_TEST_IDS.HUBPAGE_DESCRIPTION}>
        {RawHtml({ html: description })}
      </S.HubDescription>
    </ErrorBoundary>
  ) : null;

const SectionHubPageRightRail: React.FC<{
  hideAds: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  childDataLength: number;
  sidebar?: (JSX.Element | null)[];
}> = ({ hideAds, isDesktop, isMobile, childDataLength, sidebar }) => (
  <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
    <S.RightRail
      data-cy='sectionRightRail'
      className='rr-section-hub'
    >
      {!hideAds && isDesktop && (
        <RightRailFlex
          id={0}
          dataCy={getCommonTestId('RIGHT_RAIL_AD')}
        />
      )}
      {!hideAds && isMobile && <ProductHomePageInStream id={childDataLength >= 4 ? 2 : 1} />}
      {sidebar}
      {!hideAds && isDesktop && (
        <RightRailStickyAd data-cy={getCommonTestId('RIGHT_RAIL_STICKY_AD_HUB')}>
          <AdSlotToggle
            placementName='RightRailFlex'
            sticky
            index={4}
            slotId={`RightRailFlex${4}`}
          />
        </RightRailStickyAd>
      )}
      {!hideAds && isMobile && <ProductHomePageInStream id={childDataLength >= 4 ? 3 : 2} />}
    </S.RightRail>
  </ErrorBoundary>
);

// Helper to order children based on submenu order
function orderChildrenBySubmenu(children: Hub['children'], submenuItems: MenuItemProps[]): Hub['children'] {
  if (!children || !submenuItems) return children;

  const orderedChildren: Hub['children'] = [];
  // Find and add submenu items that are in children
  submenuItems.forEach((item) => {
    const submenuIsInChildren = children?.find(
      (child) => (child.uri || child.link)?.replace(/\/$/, '') === item?.url?.replace(/\/$/, ''),
    );
    if (submenuIsInChildren) {
      orderedChildren.push(submenuIsInChildren);
    }
  });

  const remainingChildren: Hub['children'] = children.filter(
    (child) => !orderedChildren.some((orderedChild) => orderedChild.uri === child.uri),
  );

  // Return ordered items first (ordered by submenu), then remaining items
  return [...orderedChildren, ...remainingChildren];
}

const SectionHubPage: React.FC<SectionHubPageProps> = ({
  data,
  dataLatest,
  pagination,
  sidebar,
  hideAds = false,
  subMenu,
  relatedRankings,
  featuredArticles,
}) => {
  const isMdMin = useBreakpoint('mdMin');
  const childData = orderChildrenBySubmenu(data.children || [], subMenu || []);
  const isDesktop = useBreakpoint('hybridMin');
  const isMobile = useBreakpoint('mdMax');

  const allSubmenuItems = getAllSubmenuItems(data, subMenu);
  const { nativo, sponsor } = data;
  const hasNativo = nativo?.nativo_enable;
  const isSponsorEnable = sponsor ? sponsor.sponsorEnable : false;
  let showLatestStories = true;
  if (data.autoclassifierFeedSection && data.parent === null) {
    showLatestStories = false;
  }

  const latestStories = dataLatest.slice(0, 8);
  const NativoHubPosts = getNativoHubPosts(hasNativo, featuredArticles.posts, isMdMin);
  const NativoChildrenCategory = getNativoChildrenCategory(childData, hasNativo);

  return (
    <S.Content>
      <SectionHubPageHeader
        data={data}
        allSubmenuItems={allSubmenuItems}
        sponsor={sponsor}
        isSponsorEnable={isSponsorEnable}
      />
      <SectionHubPageDescription description={data?.description} />
      <S.SectionsWrapper>
        {data.posts.length > 0 && (
          <ErrorBoundary fragment={UIFragments.HUB_ARTICLE_SECTION}>
            <ArticleSection
              sponsor={hasNativo}
              contentWithAd={NativoHubPosts}
              latestArticles={featuredArticles?.posts || []}
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary fragment={UIFragments.HUB_ARTICLE_BANNER}>
          {data.banner?.enabled && (
            <Banner
              enabled={data.banner.enabled}
              desktop={data.banner.bannerDesktop}
              mobile={data.banner.bannerMobile}
              link={data.banner.link}
              data-cy={HUBPAGE_TEST_IDS.HUBPAGE_BANNER}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary fragment={UIFragments.HUB_ARTICLE_SECTION}>
          {childData.length > 0 && <CategoryChild childData={NativoChildrenCategory} />}
        </ErrorBoundary>
        {showLatestStories && (
          <ErrorBoundary fragment={UIFragments.HUB_LATEST_STORIES}>
            <LatestStories
              title='Latest Stories'
              stories={latestStories}
              dataCy={SECTION_PAGE_TEST_IDS.LATEST_STORIES}
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary fragment={UIFragments.HUB_LATEST_STORIES}>
          {relatedRankings?.length > 0 && <MoreRankings relatedRankings={relatedRankings.slice(0, 3)} />}
        </ErrorBoundary>
        <ProductHomePageInStream id={childData.length >= 4 ? 1 : 0} />
        <S.BodyWrapper>
          {data.posts.length > 0 && (
            <S.ArticleWrapper>
              <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                <SectionTitle
                  tag='h2'
                  text={`More ${data.name}`}
                  titleDataCy={HUBPAGE_TEST_IDS.SECTION_TITLE_MORE}
                  className='title'
                  variant='border'
                />
              </ErrorBoundary>
              <S.LatestWrapper>
                {pagination?.showPageInfo && (
                  <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                    <S.PageInfo
                      as='h4'
                      $size={{ default: 'level7', mdMin: 'level6' }}
                      $fontFamily='graphikCond'
                    >
                      {pagination.pageInfo}
                    </S.PageInfo>
                  </ErrorBoundary>
                )}
                <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                  <LatestArticles
                    pagination={pagination}
                    title=''
                    variant={SectionVariant.LatestArticles}
                    articles={data.posts}
                    platform={Microsites.Own}
                    className='section-latest'
                    sectionType={SectionTypes.MORE_SECTION}
                    pinnedLinks={null}
                    trackingData={{
                      articleNameCD: `The Section Page ${SectionTitles.latestArticles}`,
                      contentPlacementCD: `The Section - Page - ${SectionTitles.latestArticles} grid - row`,
                    }}
                  />
                </ErrorBoundary>
              </S.LatestWrapper>
            </S.ArticleWrapper>
          )}
          <SectionHubPageRightRail
            hideAds={hideAds}
            isDesktop={isDesktop}
            isMobile={isMobile}
            childDataLength={childData.length}
            sidebar={sidebar}
          />
        </S.BodyWrapper>
      </S.SectionsWrapper>
    </S.Content>
  );
};

export default SectionHubPage;
